import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import logo from "../img/logo_white.png";
import { AutoComplete, Button, Card, Form, Input, Typography } from "antd";
import {
  LockOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import "./Signup.css";
import Footer from "../components/footer/Footer";
import LanguageMenu from "../components/languageMenu/LanguageMenu";
import { auth } from "../firebase";
import { sendEmailVerification, signInWithEmailAndPassword, signOut } from "firebase/auth";
import OlMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import { fromLonLat, toLonLat } from "ol/proj";
import "ol/ol.css";
import { useGlobalSettings } from "../context/GlobalSettingsContext";
import { isSecurePassword, isValidEmail } from "../utils/authValidation";
import { createHereTileSource, normalizeHereLanguage } from "../utils/hereMaps";
import { autosuggestAddress, geocodeAddress, reverseGeocode } from "../utils/hereGeocoding";
import { API_FleetDrivers_sign_up, API_REQUEST_HEADERS_BASEURL } from "../utils/Constants";

const centerMadrid = { lat: 40.4168, lng: -3.7038 };
const ENFORCE_PASSWORD_SECURITY = true;

export default function Signup(props) {
  const [companyName, setCompanyName] = useState("");
  const [accountOwnerName, setAccountOwnerName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordControl, setPasswordControl] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState(null);
  const [locationInput, setLocationInput] = useState("");
  const [mapCenter, setMapCenter] = useState(centerMadrid);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { hereApiKey } = useGlobalSettings();
  const resolvedMapsKey =
    process.env.REACT_APP_HERE_BROWSER_API_KEY ||
    hereApiKey ||
    "";
  const mapLanguage = normalizeHereLanguage(i18n?.resolvedLanguage || i18n?.language || "en");
  const trimmedEmail = email.trim();
  const hasValidEmail = trimmedEmail.length > 0 ? isValidEmail(trimmedEmail) : false;
  const hasSecurePassword =
    password.length > 0 ? (ENFORCE_PASSWORD_SECURITY ? isSecurePassword(password) : true) : false;
  const showInvalidEmail = trimmedEmail.length > 0 && !hasValidEmail;
  const showWeakPassword = ENFORCE_PASSWORD_SECURITY && password.length > 0 && !hasSecurePassword;
  const showPasswordMismatch = passwordControl.length > 0 && password !== passwordControl;

  useEffect(() => {
    props.funcNav(false);
  }, [props.funcNav]);

  const applyLocation = useCallback(({ name, lat, lng }) => {
    setLocation({ name, lat, lng });
    setLocationInput(name);
    setMapCenter({ lat, lng });
    setLocationError(false);
  }, []);

  const handleAddressResolve = useCallback(async (addressValue) => {
    const normalizedAddress = String(addressValue || "").trim();
    if (!normalizedAddress || !resolvedMapsKey) return null;
    const result = await geocodeAddress(normalizedAddress, resolvedMapsKey, {
      language: mapLanguage,
    });
    if (!result) return null;
    applyLocation({
      name: result.address || result.name || normalizedAddress,
      lat: result.lat,
      lng: result.lng,
    });
    return result;
  }, [applyLocation, mapLanguage, resolvedMapsKey]);

  const handleMapClick = useCallback(async ({ lat, lng }) => {
    if (!resolvedMapsKey) return;
    const result = await reverseGeocode(lat, lng, resolvedMapsKey, {
      language: mapLanguage,
    });
    const name =
      result?.address ||
      t("signup.addressFallback", { defaultValue: "Selected location" });
    applyLocation({ name, lat, lng });
  }, [applyLocation, mapLanguage, resolvedMapsKey, t]);

  const signUp = async () => {
    setErrorMessage(undefined);
    setIsSubmitting(true);
    try {
      const normalizedEmail = email.trim();
      await API_REQUEST_HEADERS_BASEURL.post(API_FleetDrivers_sign_up, {
        name: companyName,
        email: normalizedEmail,
        password,
        accountOwnerName: accountOwnerName || null,
        phone,
        vatNumber,
        location,
      });

      try {
        const userCred = await signInWithEmailAndPassword(auth, normalizedEmail, password);
        await sendEmailVerification(userCred.user, {
          url: `${window.location.origin}/login`,
          handleCodeInApp: false,
        });
      } catch (followUpError) {
        console.error("Post-signup profile sync failed:", followUpError);
        setErrorMessage(
          t("signup.verificationSendError", {
            defaultValue: "Could not send verification email right now. Please try again.",
          })
        );
        return;
      } finally {
        await signOut(auth).catch(() => {});
      }

      navigate("/login?verifyEmail=1");
    } catch (error) {
      const code = error?.code || "";
      const apiMessage = String(error?.response?.data?.message || "");
      if (code === "auth/weak-password") {
        setErrorMessage(t("signup.weakPassword") || "Password must be at least 6 characters.");
      } else if (code === "auth/invalid-email") {
        setErrorMessage(t("signup.invalidEmail") || "Invalid email address.");
      } else if (code === "auth/email-already-in-use" || /already in use/i.test(apiMessage)) {
        setErrorMessage(t("signup.emailExists") || "Email is already in use.");
      } else if (code === "auth/too-many-requests") {
        setErrorMessage(
          t("signup.verificationSendError", {
            defaultValue: "Could not send verification email right now. Please try again.",
          })
        );
      } else {
        setErrorMessage(apiMessage || "Failed to sign up. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    setErrorMessage(undefined);
    const missingRequired =
      !companyName ||
      !accountOwnerName ||
      !email ||
      !password ||
      !passwordControl ||
      !vatNumber ||
      !phone ||
      !location?.name;
    if (!location?.name) {
      setLocationError(true);
    }
    if (missingRequired) {
      setErrorMessage(
        t("signup.missingFields", { defaultValue: "Please fill all required fields." })
      );
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setErrorMessage(
        t("signup.invalidEmail", {
          defaultValue: "Please enter a valid email address.",
        })
      );
      return;
    }

    if (ENFORCE_PASSWORD_SECURITY && !isSecurePassword(password)) {
      setErrorMessage(
        t("signup.passwordSecurity", {
          defaultValue:
            "Password must have at least 8 characters, uppercase, lowercase, number, and symbol.",
        })
      );
      return;
    }

    if (password !== passwordControl) {
      setErrorMessage(
        t("signup.passwordMismatch", {
          defaultValue: "Passwords do not match.",
        })
      );
      return;
    }
    signUp();
  };

  const isFormValid =
    companyName &&
    accountOwnerName &&
    email &&
    hasValidEmail &&
    phone &&
    vatNumber &&
    location?.name &&
    password &&
    hasSecurePassword &&
    passwordControl &&
    password === passwordControl;

  return (
    <div>
      <LanguageMenu />
      <div className="signup-page">
        <div className="signup-container">
          <Typography.Title level={2} className="Gandolapp">
            <img src={logo} alt="logo" />
          </Typography.Title>
          <Card className="signup-card">
            <Form layout="vertical" onFinish={handleSubmit}>
              <div className="signup-grid">
                <Form.Item required>
                  <Input
                    name="companyName"
                    placeholder={t("signup.fleetName", { defaultValue: "Fleet name" })}
                    prefix={<UserOutlined />}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </Form.Item>

                <Form.Item required>
                  <Input
                    name="accountOwnerName"
                    placeholder={t("signup.accountOwnerName", {
                      defaultValue: "Account owner name",
                    })}
                    prefix={<UserOutlined />}
                    value={accountOwnerName}
                    onChange={(e) => setAccountOwnerName(e.target.value)}
                  />
                </Form.Item>

                <Form.Item required>
                  <Input
                    name="phone"
                    placeholder={t("signup.phone", { defaultValue: "Phone" })}
                    prefix={<PhoneOutlined />}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Form.Item>

                <Form.Item required>
                  <Input
                    name="vatNumber"
                    placeholder={t("signup.vatNumber", { defaultValue: "VAT number" })}
                    prefix={<IdcardOutlined />}
                    value={vatNumber}
                    onChange={(e) => setVatNumber(e.target.value)}
                  />
                </Form.Item>

                <div className="auth-validation-slot">
                  <Form.Item required validateStatus={showInvalidEmail ? "error" : ""}>
                    <Input
                      name="email"
                      placeholder={t("signup.email")}
                      prefix={<MailOutlined />}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Form.Item>
                  <div className={`auth-validation-message${showInvalidEmail ? " is-visible" : ""}`}>
                    {showInvalidEmail
                      ? t("signup.invalidEmail", {
                          defaultValue: "Please enter a valid email address.",
                        })
                      : "\u00A0"}
                  </div>
                </div>

                <div className="auth-validation-slot">
                  <Form.Item required validateStatus={showWeakPassword ? "error" : ""}>
                    <Input.Password
                      name="password"
                      placeholder={t("signup.password")}
                      prefix={<LockOutlined />}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </Form.Item>
                  <div className={`auth-validation-message${showWeakPassword ? " is-visible" : ""}`}>
                    {showWeakPassword
                      ? t("signup.passwordSecurity", {
                          defaultValue:
                            "Password must have at least 8 characters, uppercase, lowercase, number, and symbol.",
                        })
                      : "\u00A0"}
                  </div>
                </div>

                <div className="auth-validation-slot">
                  <Form.Item required validateStatus={showPasswordMismatch ? "error" : ""}>
                    <Input.Password
                      name="passwordControl"
                      placeholder={t("signup.confirmpassword")}
                      prefix={<LockOutlined />}
                      value={passwordControl}
                      onChange={(e) => setPasswordControl(e.target.value)}
                    />
                  </Form.Item>
                  <div className={`auth-validation-message${showPasswordMismatch ? " is-visible" : ""}`}>
                    {showPasswordMismatch
                      ? t("signup.passwordMismatch", {
                          defaultValue: "Passwords do not match.",
                        })
                      : "\u00A0"}
                  </div>
                </div>
              </div>

              <div className="signup-map-block">
                <Typography.Text strong>
                  {t("signup.address", { defaultValue: "Address" })}
                </Typography.Text>
                {resolvedMapsKey ? (
                  <SignupMapSection
                    mapsKey={resolvedMapsKey}
                    locationInput={locationInput}
                    setLocationInput={setLocationInput}
                    onAddressResolve={handleAddressResolve}
                    mapCenter={mapCenter}
                    onMapClick={handleMapClick}
                    location={location}
                    onLocationPicked={applyLocation}
                    mapLanguage={mapLanguage}
                  />
                ) : (
                  <div className="map-status">
                    {t("signup.mapMissingKey", {
                      defaultValue: "Map key missing. Configure HERE browser access.",
                    })}
                  </div>
                )}
                {locationError && (
                  <div className="error-Message">
                    {t("signup.addressRequired", { defaultValue: "Address is required." })}
                  </div>
                )}
              </div>

              {errorMessage && <div className="error-Message">{errorMessage}</div>}
              <Button type="primary" htmlType="submit" block disabled={!isFormValid} loading={isSubmitting}>
                {t("signup.signup")}
              </Button>
              <div className="signup-footer-inline">
                <span>{t("signup.alreadyHaveAccount", { defaultValue: "Already have an account?" })}</span>
                <a href="/login">{t("signup.login", { defaultValue: "Log In" })}</a>
              </div>
            </Form>
          </Card>
        </div>
      </div>
      <Footer variant="plain" />
    </div>
  );
}

const SignupMapSection = React.memo(function SignupMapSection({
  mapsKey = "",
  locationInput,
  setLocationInput,
  onAddressResolve,
  mapCenter,
  onMapClick,
  location,
  onLocationPicked,
  mapLanguage,
}) {
  const { t } = useTranslation();
  const mapHostRef = useRef(null);
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markerSourceRef = useRef(null);
  const markerFeatureRef = useRef(null);
  const onMapClickRef = useRef(onMapClick);
  const mapCenterRef = useRef(mapCenter);
  const mapLanguageRef = useRef(mapLanguage);
  const suggestedPreviewRef = useRef("");
  const suggestTimeoutRef = useRef(null);
  const suggestSequenceRef = useRef(0);
  const [addressOptions, setAddressOptions] = useState([]);

  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  useEffect(() => {
    mapCenterRef.current = mapCenter;
  }, [mapCenter]);

  useEffect(() => {
    mapLanguageRef.current = mapLanguage;
  }, [mapLanguage]);

  useEffect(() => {
    return () => {
      if (suggestTimeoutRef.current) {
        window.clearTimeout(suggestTimeoutRef.current);
        suggestTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapHostRef.current || mapRef.current || !mapsKey) return;
    const initialCenter = mapCenterRef.current || centerMadrid;

    const tileLayer = new TileLayer({
      source: createHereTileSource({
        apiKey: mapsKey,
        language: mapLanguageRef.current,
        style: "lite.day",
        imageFormat: "png8",
        includeLanguage: true,
        disablePois: true,
      }),
      visible: true,
    });
    const markerSource = new VectorSource();
    const markerLayer = new VectorLayer({
      source: markerSource,
      declutter: true,
    });

    const map = new OlMap({
      target: mapHostRef.current,
      layers: [tileLayer, markerLayer],
      view: new View({
        center: fromLonLat([initialCenter.lng, initialCenter.lat]),
        zoom: 14,
        minZoom: 2,
        maxZoom: 22,
      }),
    });

    const onMapSingleClick = (event) => {
      const [lng, lat] = toLonLat(event.coordinate);
      onMapClickRef.current?.({ lat, lng });
    };
    map.on("singleclick", onMapSingleClick);

    const updateMapSize = () => map.updateSize();
    updateMapSize();
    const resizeRafId = window.requestAnimationFrame(updateMapSize);
    const resizeTimeoutId = window.setTimeout(updateMapSize, 120);
    window.addEventListener("resize", updateMapSize);
    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => map.updateSize());
      resizeObserver.observe(mapHostRef.current);
    }

    mapRef.current = map;
    tileLayerRef.current = tileLayer;
    markerSourceRef.current = markerSource;

    return () => {
      map.un("singleclick", onMapSingleClick);
      window.removeEventListener("resize", updateMapSize);
      window.cancelAnimationFrame(resizeRafId);
      window.clearTimeout(resizeTimeoutId);
      if (resizeObserver) resizeObserver.disconnect();
      map.setTarget(undefined);
      mapRef.current = null;
      tileLayerRef.current = null;
      markerSourceRef.current = null;
      markerFeatureRef.current = null;
    };
  }, [mapsKey]);

  useEffect(() => {
    if (!mapRef.current) return;
    tileLayerRef.current?.setSource(
      createHereTileSource({
        apiKey: mapsKey,
        language: mapLanguage,
        style: "lite.day",
        imageFormat: "png8",
        includeLanguage: true,
        disablePois: true,
      })
    );
    mapRef.current.render();
  }, [mapsKey, mapLanguage]);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.getView().animate({
      center: fromLonLat([mapCenter.lng, mapCenter.lat]),
      duration: 180,
    });
  }, [mapCenter]);

  useEffect(() => {
    const source = markerSourceRef.current;
    if (!source) return;
    if (!location) {
      if (markerFeatureRef.current) {
        source.removeFeature(markerFeatureRef.current);
        markerFeatureRef.current = null;
      }
      return;
    }

    const pointGeometry = new Point(fromLonLat([location.lng, location.lat]));
    if (!markerFeatureRef.current) {
      const feature = new Feature({
        geometry: pointGeometry,
      });
      feature.setStyle(
        new Style({
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({ color: "#2563eb" }),
            stroke: new Stroke({ color: "#ffffff", width: 2 }),
          }),
          zIndex: 20,
        })
      );
      markerFeatureRef.current = feature;
      source.addFeature(feature);
      return;
    }
    markerFeatureRef.current.setGeometry(pointGeometry);
  }, [location]);

  const handleAutocompleteSearch = useCallback(
    (searchText) => {
      const normalizedSearch = String(searchText || "").trim();
      setLocationInput(searchText);

      if (suggestTimeoutRef.current) {
        window.clearTimeout(suggestTimeoutRef.current);
        suggestTimeoutRef.current = null;
      }

      if (!normalizedSearch || normalizedSearch.length < 3 || !mapsKey) {
        setAddressOptions([]);
        return;
      }

      const sequenceId = suggestSequenceRef.current + 1;
      suggestSequenceRef.current = sequenceId;

      suggestTimeoutRef.current = window.setTimeout(async () => {
        const results = await autosuggestAddress(normalizedSearch, mapsKey, {
          addressesOnly: true,
          language: mapLanguage,
          limit: 6,
          at: mapCenterRef.current,
        });

        if (suggestSequenceRef.current !== sequenceId) return;

        const firstSuggestion = results[0];
        if (firstSuggestion && mapRef.current) {
          const previewKey = `${firstSuggestion.lat.toFixed(5)},${firstSuggestion.lng.toFixed(5)}`;
          if (previewKey !== suggestedPreviewRef.current) {
            suggestedPreviewRef.current = previewKey;
            mapRef.current.getView().animate({
              center: fromLonLat([firstSuggestion.lng, firstSuggestion.lat]),
              duration: 240,
            });
          }
        }

        setAddressOptions(
          results.map((item) => {
            const label = item.address || item.name || normalizedSearch;
            return {
              value: label,
              label,
              payload: item,
            };
          })
        );
      }, 220);
    },
    [mapLanguage, mapsKey, setLocationInput]
  );

  const handleAutocompleteSelect = useCallback(
    async (value, option) => {
      const selectedAddress = String(value || "").trim();
      const selected = option?.payload || null;
      if (selected) {
        onLocationPicked({
          name: selected.address || selected.name || selectedAddress,
          lat: selected.lat,
          lng: selected.lng,
        });
      } else {
        await onAddressResolve(selectedAddress);
      }
      setAddressOptions([]);
    },
    [onAddressResolve, onLocationPicked]
  );

  const handleAddressEnter = useCallback(async () => {
    const normalizedAddress = String(locationInput || "").trim();
    if (!normalizedAddress) return;
    await onAddressResolve(normalizedAddress);
    setAddressOptions([]);
  }, [locationInput, onAddressResolve]);

  return (
    <>
      <AutoComplete
        value={locationInput}
        options={addressOptions}
        onSearch={handleAutocompleteSearch}
        onSelect={handleAutocompleteSelect}
        onChange={setLocationInput}
        filterOption={false}
      >
        <Input
          placeholder={t("signup.addressSearch", {
            defaultValue: "Search address",
          })}
          prefix={<EnvironmentOutlined />}
          onPressEnter={handleAddressEnter}
        />
      </AutoComplete>
      <div className="signup-map-container">
        <div ref={mapHostRef} className="signup-map" />
      </div>
    </>
  );
});
